import { FC, useState } from 'react'
import { useTranslate, useRefresh, useNotify } from 'react-admin'
import { Box, Button as MuiButton, CircularProgress } from '@mui/material'
import { xxllncColor } from '../layout/theme/color'
import { postJson, getUrl } from '../providers/data'

interface ToolRecord {
  enabled: boolean
}

interface ToggleAllToolsProps {
  parentResource: string
  parentId: string
  tools: ToolRecord[]
}

export const ToggleAllTools: FC<ToggleAllToolsProps> = ({ parentResource, parentId, tools }) => {
  const translate = useTranslate()
  const refresh = useRefresh()
  const notify = useNotify()
  const [loading, setLoading] = useState(false)

  if (!tools || tools.length === 0) return null

  const allEnabled = tools.every((t) => t.enabled)
  const allDisabled = tools.every((t) => !t.enabled)

  const handleToggleAll = (enable: boolean) => {
    setLoading(true)
    void postJson(`${getUrl()}/${parentResource}/${parentId}/toggle_tools`, { enabled: enable })
      .then(() => refresh())
      .catch(() => notify('ra.notification.http_error', { type: 'error' }))
      .finally(() => setLoading(false))
  }

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <MuiButton
        size="small"
        variant={allEnabled ? 'contained' : 'outlined'}
        disabled={loading || allEnabled}
        onClick={() => handleToggleAll(true)}
        sx={{
          'textTransform': 'none',
          'fontWeight': 600,
          'fontSize': 12,
          'borderColor': xxllncColor.opGreen40,
          'color': allEnabled ? xxllncColor.base : xxllncColor.green100,
          'bgcolor': allEnabled ? xxllncColor.green100 : 'transparent',
          '&:hover': {
            borderColor: xxllncColor.opGreen60,
            bgcolor: allEnabled ? xxllncColor.opGreen80 : xxllncColor.opGreen10,
          },
          '&.Mui-disabled': {
            bgcolor: allEnabled ? xxllncColor.opGreen80 : undefined,
            color: allEnabled ? xxllncColor.opLight80 : undefined,
          },
        }}
      >
        {loading ? <CircularProgress size={14} sx={{ mr: 0.5 }} /> : null}
        {translate('data.enableAll')}
      </MuiButton>
      <MuiButton
        size="small"
        variant={allDisabled ? 'contained' : 'outlined'}
        disabled={loading || allDisabled}
        onClick={() => handleToggleAll(false)}
        sx={{
          'textTransform': 'none',
          'fontWeight': 600,
          'fontSize': 12,
          'borderColor': 'rgba(0,0,0,0.15)',
          'color': allDisabled ? xxllncColor.base : 'rgba(0,0,0,0.55)',
          'bgcolor': allDisabled ? 'rgba(0,0,0,0.35)' : 'transparent',
          '&:hover': {
            borderColor: 'rgba(0,0,0,0.3)',
            bgcolor: allDisabled ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.04)',
          },
          '&.Mui-disabled': {
            bgcolor: allDisabled ? 'rgba(0,0,0,0.2)' : undefined,
            color: allDisabled ? xxllncColor.opLight80 : undefined,
          },
        }}
      >
        {translate('data.disableAll')}
      </MuiButton>
    </Box>
  )
}
